








import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { CropsDTO } from "../../api/crops/types";
import * as vegetationIndexService from "@/services/vegetation-index/index";
import {
  VegetationIndexAverage,
  VegetationIndexCrop,
} from "../../api/vegetation_index/types";
import { ma } from "moving-averages";
import { ChartColors, ChartConstants } from "@/constants/chart";
import ResizeObserver from 'resize-observer-polyfill';
import InfoTooltip from '@/components/infoTooltip/InfoTooltip.vue';
import Highcharts from 'highcharts';

@Component({
  name: "NdviChartHc",
  components: {
    InfoTooltip,
  },
})
export default class NdviChartHc extends Vue {
  @Prop({ type: String, required: true })
  private uuid!: string;

  @Prop({ type: Array, default: () => [] })
  private crops!: string[];

  @Prop({ type: String, required: false, default: "SOY" })
  private cropType!: string;

  @Prop({ type: String, required: false, default: "SUMMER" })
  private harvestSeason!: string;

  @Prop({ type: String, required: false })
  private title!: string;

  private ro: ResizeObserver | null = null;

  private chartOptions: any = {};

  private get cropsMapper() {
    const crops = (this.$store.getters[`crops/crops`](this.uuid) ??
      []) as CropsDTO[];
    return new Map(crops.map((crop) => [crop.cropUuid, `${crop.cropLabel}`]));
  }

  private get invCropsMapper() {
    const crops = (this.$store.getters[`crops/crops`](this.uuid) ??
      []) as CropsDTO[];
    return new Map(crops.map((crop) => [`${crop.cropLabel}`, crop.cropUuid]));
  }

  private beforeMount() {
    this.chartOptions = {
      chart: {
        backgroundColor: "#12191c",
        style: {
          fontFamily: ChartConstants.FONT_FAMILY,
        },
      },

      title: {
        text: `NDVI <span id="info-window-ndvi"></span>`,
        useHTML: true,
      },

      credits: {
        enabled: false
      },

      yAxis: {
        title: {
          enabled: false,
        },
        softMax: 1,
        softMin: 0,
      },

      xAxis: {
        type: "datetime",
        plotLines: [],
        plotBands: [],
      },

      tooltip: {
        shared: true,
        formatter: function(this: any): string {
          let tooltipText = Highcharts.dateFormat('%d/%m/%Y', this.x) + '<br/>';
          if (this.points) {
            this.points.forEach((point: any) => {
              const pointIndex = point.series.data.indexOf(point.point);
              const satellite = point.series.userOptions.data[pointIndex].satellite ? `(${point.series.userOptions.data[pointIndex].satellite})` : '';
              tooltipText += `<span style="color:${point.series.color}">\u25CF</span> ${point.series.name}: ${point.y} <b>${satellite}</b><br/>`;
            });
          }
          return tooltipText;
        }
      },

      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      },

      plotOptions: {
        series: {
          marker: {
            enabledThreshold: 0,
          },
          label: {
            connectorAllowed: false,
          },
        },
      },

      series: this.loadedData,

      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              legend: {
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
              },
            },
          },
        ],
      },
    };
  }

  private mounted() {
    this.redrawChartOnWillAppear();
    
    new InfoTooltip({propsData: {title: this.$t('info-tooltip.ndvi'), tooltipPlacement: 'bottom'}})
    .$mount(document.querySelector('#info-window-ndvi')!);
  }

  @Watch("hovered", { immediate: true })
  private onHover(cropLabel: string | null) {
    if (!!cropLabel) {
      this.$emit("update:hover", this.invCropsMapper.get(cropLabel));
    } else {
      this.$emit("update:hover", null);
    }
  }

  @Watch("crops", { immediate: true })
  private mountCropsData(crops: string[]) {
    if (crops.length === 0) return;
    this.chartOptions.xAxis.plotLines = []
    this.chartOptions.xAxis.plotBands = []
    crops.forEach(async (cropUuid) => {
      await vegetationIndexService.fetchAllByCropAndType(cropUuid, "NDVI");
      this.addVerticalLine(cropUuid);
    });
  }
  
  private addVerticalLine(cropUuid :string) {

    const xAxis = this.chartOptions.xAxis;
    const currentTime = new Date().getTime(); // Current date and time

    const crop = this.$store.getters[`crops/crop`](cropUuid);
    const seedingDate = crop.seedingDate.getTime();
    const color = 'rgba(100,149,237,1)';
    const colorOp = 'rgba(100,149,237,0.3)';

    // Add seeding plot line if it doesn't exist
    const seedingLineId = `seeding_${cropUuid}`;
    if (!xAxis.plotLines.some((it: any) => it.id === seedingLineId)) {
      xAxis.plotLines.push({
        id: seedingLineId,
        color: color,
        width: 3,
        value: seedingDate
      });
    }

    // Determine the end value for the plot band, either the harvest date or now
    const endValue = crop.harvestDate ? crop.harvestDate.getTime() : currentTime;

    // Add harvest plot line if it exists and doesn't already have a line
    const harvestLineId = `harvest_${cropUuid}`;
    if (crop.harvestDate && !xAxis.plotLines.some((it: any) => it.id === harvestLineId)) {
      xAxis.plotLines.push({
        id: harvestLineId,
        color: color,
        width: 3,
        value: endValue
      });
    }

    // Add a plot band between the seeding and harvest plot lines
    const plotBandId = `seeding-harvest-band_${cropUuid}`;
    
    if (!xAxis.plotBands.some((it: any) => it.id === plotBandId)) {
      xAxis.plotBands.push({
        from: seedingDate,
        to: endValue,
        color: colorOp, // Red with 50% opacity
        id: plotBandId
      });
    }

    // Update chartOptions to trigger a re-render of the chart
    this.chartOptions = { ...this.chartOptions, xAxis };
  }

  @Watch("loadedData", { immediate: true })
  private async dataChanged(data: CropsDTO) {
    this.chartOptions.series = data;
    await this.$nextTick();
  }

  @Watch("uuid", { immediate: true })
  private async mountFarmAverageData(uuid: string) {
    await this.loadIndexes(uuid);
  }

  private get averageData(): VegetationIndexAverage[] {
    return this.$store.getters[`vegetationIndex/vegetationIndexAverage`](
      this.uuid
    );
  }

  private get averageDataForCropTypeNotConfirmed(): VegetationIndexAverage[] {
    return this.$store.getters[
      `vegetationIndex/vegetationIndexAverageForCropTypeNotConfirmed`
    ](this.uuid);
  }

  private get loadedData(): any {
    const avgData = this.averageData;
    const avgDataNotConfirmed = this.averageDataForCropTypeNotConfirmed;
    const cropDataGetter = this.$store.getters["vegetationIndex/vegetationIndexCrop"] as (uuid: string) => VegetationIndexCrop[];
    const cropGetter = this.$store.getters["crops/crop"] as (uuid: string) => { };

    return [
      {
        name: this.$t("analysis.ndvi-chart.farm-label", {
          crop_type: this.$t("crop-types." + this.cropType),
        }) as string,
        data: this.movingAverage(avgData, 3).map((veg) => ({
          x: veg.x,
          y: this.roundTwoPlaces(veg.y)
        })),
        color: ChartColors.GREEN,
        events: {
          mouseOver: function () {
            // @ts-ignore
            this.update({
              color: ChartColors.LINE_HOVER,
            });
          },
          mouseOut: function () {
            // @ts-ignore
            this.update({
              color: ChartColors.GREEN,
            });
          },
        },
      },
      {
        name: this.$t("analysis.ndvi-chart.farm-label-not-confirmed") as string,
        data: this.movingAverage(avgDataNotConfirmed, 3).map((veg) => ({
          x: veg.x,
          y: this.roundTwoPlaces(veg.y),
        })),
        color: ChartColors.ORANGE,
        events: {
          mouseOver: function () {
            // @ts-ignore
            this.update({
              color: ChartColors.LINE_HOVER,
            });
          },
          mouseOut: function () {
            // @ts-ignore
            this.update({
              color: ChartColors.ORANGE,
            });
          },
        },
      },
      ...this.crops.map((cropUuid) => {
        return {
          data: cropDataGetter(cropUuid).map((veg) => ({
            x: Date.UTC(
              veg.sceneDate.getFullYear(),
              veg.sceneDate.getMonth(),
              veg.sceneDate.getDate()
            ),
            y: this.roundTwoPlaces(veg.value),
            satellite: veg.satellite
          })),
          name: `${this.cropsMapper.get(cropUuid) ?? cropUuid}`,
          color: ChartColors.WHITE,
          events: ((vm) => {
            return {
              mouseOver: function () {
                // @ts-ignore
                this.update({
                  color: ChartColors.OFF_WHITE,
                });
                vm.$emit("update:hover", cropUuid);
              },
              mouseOut: function () {
                // @ts-ignore
                this.update({
                  color: ChartColors.WHITE,
                });
                vm.$emit("update:hover", null);
              },
            };
          })(this),
        };
      }),
    ];
  }

  private movingAverage(
    avgData: VegetationIndexAverage[],
    movingFactor: number
  ) {
    const avgMa = ma(
      avgData.map((veg) => this.roundTwoPlaces(veg.average)),
      movingFactor
    );

    let avgDt = avgData.map((veg) => veg.sceneDate);

    const result: { x: number; y: number }[] = [];
    avgDt.forEach((key: Date, i: number) =>
      result.push({
        x: Date.UTC(key.getFullYear(), key.getMonth(), key.getDate()),
        y: avgMa[i],
      })
    );

    return result;
  }

  private roundTwoPlaces(num: number) {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  private async loadIndexes(uuid: string) {
    await Promise.all([
      vegetationIndexService.fetchAllAveragesByAnalysisAndType(
        uuid,
        "NDVI",
        this.cropType,
        this.harvestSeason
      ),
      vegetationIndexService.fetchAllAveragesByAnalysisForCrotTypeNotConfirmed(
        uuid,
        "NDVI",
        this.cropType,
        this.harvestSeason
    )]);
  }

  private redrawChartOnWillAppear() {
    let lastHeight = 0;
    let lastWidth = 0;

    this.ro = new ResizeObserver((entries, observer) => {
      for (const entry of entries) {
        const { height, width } = entry.contentRect;
        if ((height !== lastHeight  || width !== lastWidth) && !!this.averageData) {
          this.$nextTick(() => {
            this.reflow();
          });
        }
        lastHeight = height;
        lastWidth = width
      }
    });
    this.ro.observe((this.$refs.hcComponent! as Vue).$el);
  }

  public reflow() {
    // @ts-ignore
    this.$refs.hcComponent.chart.reflow();
  }
}
